import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { computed, inject } from '@angular/core';
import { IndicatorTreeNode } from '../models/IndicatorTreeNode';
import { DataLayerType } from '../models/data-layer-type';
import { DataLayer } from '../models/data-layer';
import { Indicator } from '../models/indicator';
import { DataLayerStore } from './data-layer/data-layer.state';
import { GeoDataStore } from './geo/geo-data.state';
import { GeoData } from '../models/geo-data';
import { DataLayerTypeStore } from './data-layer-type/data-layer-type.state';
import {
  getDistrictsFromSelection,
  getFeatureCollectionFromSelection,
} from './geo/geo-utils';
import { IndicatorStore } from './indicator/indicator.state';
import { IndicatorDataStore } from './indicatpr-data/indicator-data.state';
import { Commune } from '../models/commune';
import { CommuneStore } from './commune/commune.state';
import { Topic } from '../models/topic';
import { CustomGridStackWidget } from '../services/grid-widget-config.service';
import { units, UnitStyle } from '../models/unit';
import { AppUtilityService } from '../utility/app.utility';
import { ScaleService } from '../services/scale.service';
import { ThemeCssClass } from '../components/atoms/theme-switcher/theme-switcher.component';

export type AtlasEventCallback = {
  key: string;
  options: AtlasEventOptions;
};

export type AtlasEventOptions = {
  dataLayerId?: string;
  dataLayerTypeId?: string;
  poiLayerTypeId?: string;
  indicatorIds?: string[];
  bookmarkId?: string;
  storyId?: string;
  topic?: Topic;
  widgetCopy?: CustomGridStackWidget;
  template?: Partial<CustomGridStackWidget>[];
  widgetId?: string;
  key?: string;
  type?: string;
};

export type AtlasEventKey =
  | 'generateView'
  | 'addToAtlas'
  | 'loadBookmark'
  | 'loadStory'
  | 'markDistrict'
  | 'loadTopic'
  | 'openSearch'
  | 'openLandingPage'
  | 'closeLandingPage'
  | 'pasteWidgetCopy'
  | 'addLayerDialog'
  | 'addColorsetDialog'
  | 'addBookmarkCategoryDialog'
  | 'addWorkspaceDialog'
  | 'highlightDistrict'
  | string;

export type GlobalsState = {
  treeInitialized: boolean;
  indicatorTree: IndicatorTreeNode[];
  selectedType: DataLayerType | null;
  selectedLayer: DataLayer | null;
  selectedWidget: string | null;
  selectedIndicator: Indicator | null;
  selectedChildIndicator: Indicator | null;
  selectedGeoData: GeoData | null;
  lastInsertedId: string;
  activeCommune: Commune | null;
  activeBookmark: string | null;
  activeStory: string | null;
  activeTheme: ThemeCssClass;
  activeTopic: Topic | null;
  activeReport: {
    districtKey: string;
    dataLayerId: string;
  } | null;
  openReport: boolean;
  atlasEventCallback: AtlasEventCallback | null;
  availableHelpByRoute: string | null;
};

const initialState: GlobalsState = {
  treeInitialized: false,
  indicatorTree: [],
  selectedType: null,
  selectedLayer: null,
  selectedWidget: null,
  selectedIndicator: null,
  selectedChildIndicator: null,
  selectedGeoData: null,
  lastInsertedId: '',
  activeCommune: null,
  activeBookmark: null,
  activeStory: null,
  activeTheme: 'hyperion-light',
  activeTopic: null,
  activeReport: null,
  openReport: false,
  atlasEventCallback: null,
  availableHelpByRoute: null,
};

export const GlobalsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed(
    ({ treeInitialized, indicatorTree, selectedGeoData, activeCommune }) => ({
      isTreeInitialized: computed(() => treeInitialized()),
      getIndicatorTree: computed(() => indicatorTree()),
      communeConfiguration: computed(() => activeCommune()?.atlasConfig),
      getDistrictsFromSelection: getDistrictsFromSelection(selectedGeoData),
      getFeatureCollectionFromSelection:
        getFeatureCollectionFromSelection(selectedGeoData),
      sourceString: computed(() => `Keck-Atlas, ${activeCommune()?.name}`),
    })
  ),
  withMethods(
    (
      store,
      layerStore = inject(DataLayerStore),
      layerTypeStore = inject(DataLayerTypeStore),
      indicatorStore = inject(IndicatorStore),
      indicatorDataStore = inject(IndicatorDataStore),
      geoDataStore = inject(GeoDataStore),
      communeStore = inject(CommuneStore),
      utility = inject(AppUtilityService),
      scalesService = inject(ScaleService)
    ) => ({
      getBaseConfig() {
        return communeStore.baseConfig();
      },
      setTreeInitialized(tree: IndicatorTreeNode[]) {
        patchState(store, { treeInitialized: true, indicatorTree: tree });
      },
      unsetTree() {
        patchState(store, { treeInitialized: false, indicatorTree: [] });
      },
      setSelectedType(type: DataLayerType, skipLayerSelection = false) {
        patchState(store, { selectedType: type });
        // select first layer of selected type
        const layerGroup = layerStore.groupedByType()[type._id];
        if (skipLayerSelection) return;
        const layer = layerGroup && layerGroup.length ? layerGroup[0] : null;
        this.setSelectedLayer(layer);
      },
      setSelectedLayer(layer: DataLayer | null) {
        patchState(store, { selectedLayer: layer });
        if (layer) {
          if (layer.geoData) {
            const geoDataFromLayer =
              geoDataStore.items()[layer.geoData] || null;
            this.setSelectedGeoData(geoDataFromLayer);
          } else {
            this.setSelectedGeoData(null);
          }

          if (store.selectedType()?._id === layer.parent) return;
          this.setSelectedType(layerTypeStore.getById(layer.parent), true);
        }
      },

      setSelectedWidget(selectedWidget: string | null) {
        patchState(store, { selectedWidget });
      },

      setTheme(activeTheme: ThemeCssClass) {
        patchState(store, { activeTheme });
      },

      setSelectedIndicator(indicator: Indicator | null) {
        patchState(store, { selectedIndicator: indicator });
        if (indicator && indicator.isSet) {
          patchState(store, {
            selectedChildIndicator: indicatorStore.filterByParent(
              indicator._id
            )[0],
          });
        } else if (!indicator || !indicator.isSet) {
          patchState(store, { selectedChildIndicator: null });
        }
      },
      setSelectedChildIndicator(indicator: Indicator | null) {
        patchState(store, { selectedChildIndicator: indicator });
      },
      setSelectedGeoData(geoData: GeoData | null) {
        patchState(store, { selectedGeoData: geoData });
        // also set geoDataStore - important for import/export data
        patchState(geoDataStore, { selectedId: geoData ? geoData._id : '' });
      },
      getActiveIndicatorData() {
        const indicator = store.selectedIndicator();
        const childIndicator = store.selectedChildIndicator();
        const layer = store.selectedLayer();
        return indicator && layer
          ? indicatorDataStore.getByDataLayerAndIndicator(
              layer._id,
              childIndicator?._id ?? indicator._id
            )
          : undefined;
      },
      setActiveStory(storyId: string) {
        if (!document.fullscreenElement) {
          document.documentElement.requestFullscreen().catch(e => {
            console.error(
              `Error attempting to enable fullscreen mode: ${e.message}`
            );
          });
        }
        patchState(store, { activeStory: storyId });
      },
      closeActiveStory() {
        if (document.fullscreenElement) {
          document.exitFullscreen().catch(e => {
            console.error(
              `Error attempting to exit fullscreen mode: ${e.message}`
            );
          });
        }
        patchState(store, { activeStory: null });
      },
      setOpenReport() {
        patchState(store, { openReport: true });
      },
      setActiveReport(dataLayerId: string, districtKey: string) {
        patchState(store, {
          activeReport: { districtKey, dataLayerId },
          openReport: true,
        });
      },
      closeActiveReport() {
        patchState(store, { activeReport: null, openReport: false });
      },
      setActiveBookmark(bookmark: string) {
        patchState(store, { activeBookmark: bookmark });
      },
      closeActiveBookmark() {
        patchState(store, { activeBookmark: null });
      },
      setActiveTopic(topic: Topic) {
        patchState(store, { activeTopic: topic });
      },
      closeActiveTopic() {
        patchState(store, { activeTopic: null });
      },
      closeAll() {
        patchState(store, {
          activeStory: null,
          activeReport: null,
          openReport: false,
          activeBookmark: null,
          activeTopic: null,
        });
      },
      triggerAtlasEvent(key: AtlasEventKey, options: AtlasEventOptions) {
        patchState(store, {
          atlasEventCallback: { key, options },
        });
        setTimeout(() => {
          patchState(store, { atlasEventCallback: null });
        });
      },
      formatPlainValue(value: number | null, decimals = 1) {
        if (!value) return 'k. A.';
        return `${utility.formatNumberToGerman(value, decimals)}`;
      },
      getDecimals(
        unit?: string | number,
        indicator?: Indicator,
        isWegweiserIndicator = false
      ): number {
        const communeConfiguration = store.communeConfiguration();
        let decimals: number | undefined = undefined;
        if (isWegweiserIndicator) {
          decimals =
            unit === '%'
              ? communeConfiguration?.relativeDecimals
              : communeConfiguration?.absoluteDecimals;
        } else {
          decimals =
            indicator?.decimals !== undefined && indicator?.decimals !== null
              ? indicator.decimals
              : Number(indicator?.unit) !== 3
                ? communeConfiguration?.absoluteDecimals
                : communeConfiguration?.relativeDecimals;
        }

        return decimals || 0;
      },
      getFormattedValue(
        value: number | null,
        indicatorId: string,
        skipUnit = false,
        unit?: string,
        unitStyle: UnitStyle = 'short',
        isWegweiserIndicator = false
      ) {
        if (value === null || isNaN(value)) return 'k. A.';

        if (isWegweiserIndicator) {
          const decimals = this.getDecimals(unit, undefined, true);
          return skipUnit
            ? `${utility.formatNumberToGerman(value, decimals)}`
            : `${utility.formatNumberToGerman(value, decimals)} ${unit || ''}`;
        }

        const indicator = indicatorStore.getById(indicatorId);
        if (!indicator) return 'k. A.';
        if (indicator.unit === '99' && indicator.legend) {
          return (
            scalesService.getScaleLabel(value, indicator.legend) || 'k. A.'
          );
        } else {
          const decimals = this.getDecimals(unit, indicator);

          return skipUnit
            ? `${utility.formatNumberToGerman(value, decimals)}`
            : `${utility.formatNumberToGerman(value, decimals)} ${unit || units[indicator.unit][unitStyle]}`;
        }
      },
      async setActiveCommune() {
        const commune = await communeStore.getActiveCommune();
        patchState(store, { activeCommune: commune });
      },
    })
  ),
  withHooks({
    onInit({ setActiveCommune }) {
      setActiveCommune();
    },
    onDestroy() {
      console.log('on destroy');
    },
  })
);
